import React, { useState } from 'react';
import { FileDown, Building2, Calendar, CreditCard } from 'lucide-react';
import { jsPDF } from 'jspdf';
import DateInput from './DateInput';
import './AppointmentOrderForm.css';

const mockFormData = {
  companyName: "ABC Manufacturing Pvt Ltd",
  bankName: "HDFC Bank",
  accountNumber: "50200012345678",
  ifscCode: "HDFC0001234",
  month: "July",
  year: "2025",
  openingBalance: "250000",
  closingBalance: "305000",

  transactions: [
    { date: "2025-07-01", particulars: "Opening Balance", debit: "", credit: "", balance: "250000" },
    { date: "2025-07-05", particulars: "Salary Payment - July 2025", debit: "185000", credit: "", balance: "65000" },
    { date: "2025-07-10", particulars: "Revenue Received", debit: "", credit: "300000", balance: "365000" },
    { date: "2025-07-15", particulars: "PF Payment", debit: "45000", credit: "", balance: "320000" },
    { date: "2025-07-25", particulars: "Vendor Payment", debit: "15000", credit: "", balance: "305000" }
  ],

  preparedBy: "Finance Manager",
  place: "Bangalore",
  date: "2025-07-31"
};

const BankStatementForm = () => {
  const [formData, setFormData] = useState(mockFormData);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleTransactionChange = (index, field, value) => {
    const updatedTrans = [...formData.transactions];
    updatedTrans[index] = { ...updatedTrans[index], [field]: value };
    setFormData(prev => ({ ...prev, transactions: updatedTrans }));
  };

  const generatePDF = async () => {
    setIsGenerating(true);

    try {
      const doc = new jsPDF();

      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.text('BANK STATEMENT', 105, 15, { align: 'center' });
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text('For Salary Transfer Records', 105, 22, { align: 'center' });

      let yPos = 35;
      const leftMargin = 15;

      doc.setFontSize(9);
      doc.text('Company:', leftMargin, yPos);
      doc.setFont(undefined, 'bold');
      doc.text(formData.companyName, leftMargin + 25, yPos);
      yPos += 5;

      doc.setFont(undefined, 'normal');
      doc.text('Bank:', leftMargin, yPos);
      doc.setFont(undefined, 'bold');
      doc.text(formData.bankName, leftMargin + 15, yPos);
      yPos += 5;

      doc.setFont(undefined, 'normal');
      doc.text('Account No:', leftMargin, yPos);
      doc.setFont(undefined, 'bold');
      doc.text(formData.accountNumber, leftMargin + 28, yPos);
      doc.setFont(undefined, 'normal');
      doc.text('IFSC:', leftMargin + 90, yPos);
      doc.setFont(undefined, 'bold');
      doc.text(formData.ifscCode, leftMargin + 103, yPos);
      yPos += 5;

      doc.setFont(undefined, 'normal');
      doc.text('Period:', leftMargin, yPos);
      doc.setFont(undefined, 'bold');
      doc.text(`${formData.month} ${formData.year}`, leftMargin + 20, yPos);
      yPos += 10;

      // Table
      doc.setFontSize(8);
      doc.setFont(undefined, 'bold');
      const cols = { date: leftMargin, particulars: leftMargin + 25, debit: leftMargin + 110, credit: leftMargin + 140, balance: leftMargin + 170 };

      doc.text('Date', cols.date, yPos);
      doc.text('Particulars', cols.particulars, yPos);
      doc.text('Debit', cols.debit, yPos);
      doc.text('Credit', cols.credit, yPos);
      doc.text('Balance', cols.balance, yPos);

      yPos += 2;
      doc.line(leftMargin, yPos, 195, yPos);
      yPos += 5;

      doc.setFont(undefined, 'normal');
      formData.transactions.forEach((txn) => {
        doc.text(txn.date, cols.date, yPos);
        doc.text(txn.particulars.substring(0, 40), cols.particulars, yPos);
        doc.text(txn.debit || '-', cols.debit, yPos);
        doc.text(txn.credit || '-', cols.credit, yPos);
        doc.text(txn.balance, cols.balance, yPos);
        yPos += 5;
      });

      yPos += 3;
      doc.line(leftMargin, yPos, 195, yPos);
      yPos += 10;

      doc.setFont(undefined, 'bold');
      doc.text('Opening Balance: Rs. ' + formData.openingBalance, leftMargin, yPos);
      yPos += 6;
      doc.text('Closing Balance: Rs. ' + formData.closingBalance, leftMargin, yPos);
      yPos += 10;

      doc.setFontSize(8);
      doc.setFont(undefined, 'normal');
      doc.text('Prepared By: ' + formData.preparedBy, leftMargin, yPos);
      yPos += 6;
      doc.text('Place: ' + formData.place, leftMargin, yPos);
      doc.text('Date: ' + formData.date, leftMargin + 80, yPos);

      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(`Generated on: ${new Date().toLocaleString()}`, 105, 285, { align: 'center' });
      doc.text('PayrollGen - Bank Statement', 105, 290, { align: 'center' });

      doc.save(`BankStatement_${formData.month}_${formData.year}.pdf`);

      setTimeout(() => {
        setIsGenerating(false);
        alert('Bank Statement PDF generated successfully!');
      }, 500);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
      setIsGenerating(false);
    }
  };

  return (
    <div className="appointment-order-container">
      <div className="appointment-header">
        <div>
          <h2 className="appointment-title">Bank Statement</h2>
          <p className="appointment-subtitle">Bank Statement Format for Salary Transfer Records</p>
        </div>
      </div>

      <div className="appointment-content">
        <div className="info-alert">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="16" x2="12" y2="12"/>
            <line x1="12" y1="8" x2="12.01" y2="8"/>
          </svg>
          <span>Review the bank statement details below.</span>
        </div>

        <div className="form-section">
          <div className="section-header">
            <Building2 size={20} />
            <h3>Company & Bank Details</h3>
          </div>
          <div className="form-grid">
            <div className="form-field">
              <label>Company Name</label>
              <input type="text" value={formData.companyName} onChange={(e) => handleInputChange('companyName', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Bank Name</label>
              <input type="text" value={formData.bankName} onChange={(e) => handleInputChange('bankName', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Account Number</label>
              <input type="text" value={formData.accountNumber} onChange={(e) => handleInputChange('accountNumber', e.target.value)} />
            </div>
            <div className="form-field">
              <label>IFSC Code</label>
              <input type="text" value={formData.ifscCode} onChange={(e) => handleInputChange('ifscCode', e.target.value)} />
            </div>
          </div>
        </div>

        <div className="form-section">
          <div className="section-header">
            <Calendar size={20} />
            <h3>Period & Balance</h3>
          </div>
          <div className="form-grid">
            <div className="form-field">
              <label>Month</label>
              <select value={formData.month} onChange={(e) => handleInputChange('month', e.target.value)}>
                {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label>Year</label>
              <input type="number" value={formData.year} onChange={(e) => handleInputChange('year', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Opening Balance (₹)</label>
              <input type="number" value={formData.openingBalance} onChange={(e) => handleInputChange('openingBalance', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Closing Balance (₹)</label>
              <input type="number" value={formData.closingBalance} onChange={(e) => handleInputChange('closingBalance', e.target.value)} />
            </div>
          </div>
        </div>

        <div className="form-section">
          <div className="section-header">
            <CreditCard size={20} />
            <h3>Transactions</h3>
          </div>

          {formData.transactions.map((txn, idx) => (
            <div key={idx} style={{ background: 'var(--theme-surface)', padding: '1rem', borderRadius: '8px', marginBottom: '1rem', border: '1px solid #e5e7eb' }}>
              <div className="form-grid">
                <div className="form-field">
                  <label>Date</label>
                  <DateInput value={txn.date} onChange={(e) => handleTransactionChange(idx, 'date', e.target.value)} />
                </div>
                <div className="form-field full-width">
                  <label>Particulars</label>
                  <input type="text" value={txn.particulars} onChange={(e) => handleTransactionChange(idx, 'particulars', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Debit (₹)</label>
                  <input type="number" value={txn.debit} onChange={(e) => handleTransactionChange(idx, 'debit', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Credit (₹)</label>
                  <input type="number" value={txn.credit} onChange={(e) => handleTransactionChange(idx, 'credit', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Balance (₹)</label>
                  <input type="number" value={txn.balance} onChange={(e) => handleTransactionChange(idx, 'balance', e.target.value)} />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="form-section">
          <div className="section-header">
            <FileDown size={20} />
            <h3>Approval Details</h3>
          </div>
          <div className="form-grid">
            <div className="form-field">
              <label>Prepared By</label>
              <input type="text" value={formData.preparedBy} onChange={(e) => handleInputChange('preparedBy', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Place</label>
              <input type="text" value={formData.place} onChange={(e) => handleInputChange('place', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Date</label>
              <DateInput value={formData.date} onChange={(e) => handleInputChange('date', e.target.value)} />
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button onClick={generatePDF} disabled={isGenerating} className={`btn-generate ${isGenerating ? 'generating' : ''}`}>
            {isGenerating ? (
              <>
                <svg className="spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" opacity="0.25"/>
                  <path d="M12 2a10 10 0 0 1 0 20" opacity="0.75"/>
                </svg>
                <span>Generating PDF...</span>
              </>
            ) : (
              <>
                <FileDown size={20} />
                <span>Generate & Download Bank Statement PDF</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BankStatementForm;
